import React from 'react';

interface DieFaceProps {
  value: number;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  onClick?: () => void;
  selected?: boolean;
  className?: string;
  variant?: 'default' | 'control'; // control variant for bidding interface
}

const DieFace: React.FC<DieFaceProps> = ({
  value,
  size = 'md',
  onClick,
  selected = false,
  className = '',
  variant = 'default'
}) => {
  // Dice dot patterns
  const patterns: { [key: number]: number[][] } = {
    1: [[1, 1]],
    2: [[0, 0], [2, 2]],
    3: [[0, 0], [1, 1], [2, 2]],
    4: [[0, 0], [0, 2], [2, 0], [2, 2]],
    5: [[0, 0], [0, 2], [1, 1], [2, 0], [2, 2]],
    6: [[0, 0], [0, 2], [1, 0], [1, 2], [2, 0], [2, 2]],
  };

  const pattern = patterns[value] || [];

  const sizeClasses = {
    xs: 'w-10 h-10',
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-20 h-20',
  };

  const dotSizeClasses = {
    xs: 'w-1 h-1',
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5',
  };

  // Color variants
  const variantClasses = {
    default: 'bg-white dark:bg-gray-100',
    control: 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700',
  };

  const dotColorClasses = {
    default: 'bg-gray-900 dark:bg-gray-800',
    control: 'bg-primary-700 dark:bg-primary-300',
  };

  const baseClasses = onClick
    ? 'cursor-pointer transition-all hover:shadow-lg active:scale-95'
    : '';

  const selectedClasses = selected
    ? 'ring-4 ring-primary-500 dark:ring-primary-400 shadow-lg'
    : '';

  return (
    <div
      onClick={onClick}
      className={`
        ${sizeClasses[size]}
        ${baseClasses}
        ${selectedClasses}
        ${className}
        ${variantClasses[variant]}
        rounded-lg shadow-md p-2 relative
      `}
    >
      <div className="grid grid-cols-3 grid-rows-3 gap-1 h-full">
        {Array.from({ length: 9 }).map((_, idx) => {
          const row = Math.floor(idx / 3);
          const col = idx % 3;
          const hasDot = pattern.some(([r, c]) => r === row && c === col);

          return (
            <div key={idx} className="flex items-center justify-center">
              {hasDot && (
                <div className={`${dotSizeClasses[size]} ${dotColorClasses[variant]} rounded-full`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DieFace;
